import { isPlatformBrowser } from '@angular/common';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Inject, Injectable, PLATFORM_ID } from '@angular/core';
import * as moment from 'moment/moment';
import { environment } from '../../../environments/environment';
import { Portfolio } from '../../../interfaces/idefend/portfolio.interface';
import { PortfolioGroups } from '../../../interfaces/idefend/portfolio-group.interface';
import { Currency } from '../../../interfaces/idefend/currency.interface';
import { LocalizationSetting } from '../../../interfaces/idefend/localization-settings.interface';
import { SodanReplayHeads } from '../../../interfaces/idefend/sodan-replay-heads.interface';
import { PolicyGroupPayload } from '../../../interfaces/idefend/policy-groups-payload.interface';
import { PolicyGroupResponse } from '../../../interfaces/idefend/policy-groups-response.interface';
import { QuestionManagementReplyPayload } from '../../../interfaces/idefend/question-reply-payload.interface';
import { ReplyResponse } from '../../../interfaces/idefend/question-reply-response.interface';
import { PaginationParams } from '../../../interfaces/idefend/pagination-param.interface';
import { VehiclesCategory } from '../../../interfaces/idefend/vehilces-category.interface';
import { VehicleUsage } from '../../../interfaces/idefend/vehicle-usage.interface';
import { combineLatest, Observable } from 'rxjs';
import { VehicleMake } from '../../../interfaces/idefend/vehicle-make.interface';
import { VehicleModel } from '../../../interfaces/idefend/vehicle-model.interface';
import { CalculateOfferPayload } from '../shared/interfaces/calculate-offer-payload.interface';
import { CalculateOfferResponse } from '../shared/interfaces/calculate-offer-response.interface';
import { CreationLockPayload } from '../shared/interfaces/creation-lock-payload.interface';
import { CreationLockResponse } from '../shared/interfaces/creation-lock-response.interface';
import { ConfirmSignaturePayload } from '../../../interfaces/idefend/confirm-signature-payload.interface';
import { take } from 'rxjs/operators';
import { mapOfferLockPayload } from './helpers/offer-lock-payload-mapper';
import { GetPolicy, LockOffer, SignOffer } from './store/offer.action';
import { Store } from '@ngxs/store';
import { OfferState } from './store/offer.state';
import { PolicyCreatorState } from './store/policy-creator.state';
import { Router } from '@angular/router';
import * as Sentry from '@sentry/angular';

@Injectable({
  providedIn: 'root',
})
export class PolicyCreatorService {
  private apiBase = environment.apiBase;
  public characterPattern = /\p{L}/u;
  public digitOrSymbolPattern = /\P{L}/gu;
  public isDialogOpen = false;
  public allowedPortfolioProducts = [
    {
      productCode: '5_DCGAP_M25_GEN',
      productDerivativeAlias: 'MAX',
      order: 1,
    },
    {
      productCode: '5_DCGAP_MG25_GEN',
      productDerivativeAlias: 'MAX AC',
      order: 2,
    },
    {
      productCode: '5_DTGAP_M25_GEN',
      productDerivativeAlias: 'T-MAX',
      order: 3,
    },
    {
      productCode: '4_DTGAP_MG25_GEN',
      productDerivativeAlias: 'T-MAX AC',
      order: 4,
    },
    {
      productCode: '5_DCGAP_F25_GEN',
      productDerivativeAlias: 'FLEX',
      order: 5,
    },
  ];

  intervalId;

  constructor(
    @Inject(PLATFORM_ID) private platformId: Object,
    private http: HttpClient,
    private store: Store,
    private router: Router,
  ) {}

  handleCityValidation(event: KeyboardEvent) {
    const pattern =
      /^([a-zA-Z\u0080-\u024F]+(?:. |-| |'))*[a-zA-Z\u0080-\u024F]*$/gm;
    // @ts-ignore-next-line
    let value = event.target.value;

    while (!pattern.test(value)) {
      value = value.slice(0, -1);
    }

    // @ts-ignore-next-line
    event.target.value = value;
  }

  filterNonAlphaChars(event) {
    if (event.key.length === 1) {
      if (!this.characterPattern.test(event.key)) {
        event.target.value = event.target.value.slice(
          0,
          event.target.value.length - 1,
        );
      }
    }

    if (this.digitOrSymbolPattern.test(event.target.value)) {
      event.target.value = event.target.value.slice(
        0,
        event.target.value.search(this.digitOrSymbolPattern),
      );
    }
  }

  clearInterval(): void {
    clearInterval(this.intervalId);
  }

  fetchJwtToken(): Observable<void> {
    return this.http.get<void>(`${this.apiBase}/idefend/jwt-token`);
  }

  /* Initial APK steps */
  getPortfolios(): Observable<Portfolio[]> {
    return this.http.get<Portfolio[]>(`${this.apiBase}/idefend/portfolios`);
  }

  getGroups(): Observable<PortfolioGroups[]> {
    return this.http.get<PortfolioGroups[]>(
      `${this.apiBase}/idefend/product-portfolio/groups`,
    );
  }

  getCurrency(id: number): Observable<Currency> {
    return this.http.get<Currency>(`${this.apiBase}/idefend/currencies/${id}`);
  }

  getLocalizationSettings(id: number): Observable<LocalizationSetting> {
    return this.http.get<LocalizationSetting>(
      `${this.apiBase}/idefend/localization-settings/${id}`,
    );
  }

  setSodanReplyHeads(): Observable<SodanReplayHeads> {
    return this.http.post<SodanReplayHeads>(
      `${this.apiBase}/idefend/sodan-reply-heads`,
      {},
    );
  }

  scrollToTop() {
    setTimeout(() => {
      if (window.scrollY !== 0) {
        window.scrollTo(0, 0);
      }
    }, 100);
  }

  setPolicyGroups(
    payload: PolicyGroupPayload,
  ): Observable<PolicyGroupResponse> {
    return this.http.post<PolicyGroupResponse>(
      `${this.apiBase}/idefend/policy-groups`,
      payload,
    );
  }

  patchQuestionReply(
    payload: QuestionManagementReplyPayload,
    query: { locale: string },
    id: number,
  ): Observable<ReplyResponse> {
    return this.http.put<ReplyResponse>(
      `${this.apiBase}/idefend/question-management/replies/${id}`,
      payload,
      {
        params: query,
      },
    );
  }
  /* Initial APK steps */

  getVehicleCategories(
    query: PaginationParams,
  ): Observable<VehiclesCategory[]> {
    const queryDto = new HttpParams({
      // @ts-ignore-next-line
      fromObject: query,
    });

    return this.http.get<VehiclesCategory[]>(
      `${this.apiBase}/idefend/vehicles/categories`,
      {
        params: queryDto,
      },
    );
  }

  getVehicleUsages(query: PaginationParams): Observable<VehicleUsage[]> {
    const queryDto = new HttpParams({
      // @ts-ignore-next-line
      fromObject: query,
    });

    return this.http.get<VehicleUsage[]>(
      `${this.apiBase}/idefend/vehicles/usages`,
      {
        params: queryDto,
      },
    );
  }

  getVehicleMakes(code?: string): Observable<VehicleMake[]> {
    const queryDto = new HttpParams({
      // @ts-ignore-next-line
      fromObject: {
        categoryCode: code ?? '',
      },
    });

    return this.http.get<VehicleMake[]>(
      `${this.apiBase}/idefend/vehicles/makes`,
      {
        params: queryDto,
      },
    );
  }

  getVehicleModels(): Observable<VehicleModel[]> {
    return this.http.get<VehicleModel[]>(
      `${this.apiBase}/idefend/vehicles/models`,
    );
  }

  getOffer(payload: CalculateOfferPayload): Observable<CalculateOfferResponse> {
    return this.http.post<CalculateOfferResponse>(
      `${this.apiBase}/idefend/policies/creation/calculate-offer`,
      payload,
    );
  }

  lockOffer(payload: CreationLockPayload): Observable<CreationLockResponse> {
    return this.http.post<CreationLockResponse>(
      `${this.apiBase}/idefend/policies/creation/lock`,
      payload,
    );
  }

  /* TODO: check response */
  signOffer(id: number, payload: ConfirmSignaturePayload): Observable<unknown> {
    return this.http.put(
      `${this.apiBase}/idefend/policies/${id}/confirm-signature`,
      payload,
    );
  }

  getPolicyByNumber(policyNo: string) {
    console.log(`[PolicyCreatorService] Getting policy by number: ${policyNo}`);
    return this.http.get<CreationLockResponse[]>(
      `${this.apiBase}/idefend/policies?policyNo=${policyNo}`,
    );
  }

  getPolicy(id: number): Observable<CreationLockResponse> {
    return this.http.get<CreationLockResponse>(
      `${this.apiBase}/idefend/policies/${id}`,
    );
  }

  getAttachments(policyId: number, documentType: string): Observable<any> {
    return this.http.get(
      `${this.apiBase}/idefend/policies/${policyId}/document-download/${documentType}`,
    );
  }

  checkPopupBlocker() {
    if (isPlatformBrowser(this.platformId)) {
      try {
        const testPopup = window.open('', '_blank', 'width=1,height=1');
        if (testPopup === null || typeof testPopup === 'undefined') {
          Sentry.captureMessage('# POPUP BLOCKED!');
          console.error('# POPUP BLOCKED!');
          alert(
            'Wyskakujące okienka są zablokowane w Twojej przeglądarce. Prosimy odblokować okienka aby przejść do płatności.',
          );
          // Pop-ups are blocked
          return true;
        } else {
          // Pop-ups are allowed
          console.log('# POPUP ALLOWED!');
          testPopup.close();
          return false;
        }
      } catch (e) {
        Sentry.captureException(e);
      }
    }
  }

  handlePolicySigning(): void {
    console.log('[Payment Flow Service] Starting policy signing process');

    const policyCreatorState$ = this.store.select(PolicyCreatorState.state);
    const offer$ = this.store.select(OfferState.offer);
    const offerLocked$ = this.store.select(OfferState.offerLocked);

    Sentry.addBreadcrumb({
      category: 'payment',
      message: 'handlePolicySigning method called in service',
      level: 'info',
    });

    combineLatest([policyCreatorState$, offer$])
      .pipe(take(1))
      .subscribe(([policyCreatorState, offerState]) => {
        console.log(
          '[Payment Flow Service] Got policy creator state and offer state',
          {
            hasState: !!policyCreatorState,
            hasOffer: !!offerState,
          },
        );

        const payload = mapOfferLockPayload(policyCreatorState, offerState);

        if (payload) {
          console.log(
            '[Payment Flow Service] Mapped offer lock payload:',
            payload,
          );
          Sentry.addBreadcrumb({
            category: 'payment',
            message: 'Offer lock payload generated',
            level: 'info',
            data: {
              payloadSignatureType: payload.signatureTypeCode,
            },
          });

          const paymentMethod = this.store.selectSnapshot(
            PolicyCreatorState.paymentMethod,
          );
          console.log(
            `[Payment Flow Service] Using payment method: ${paymentMethod}`,
          );

          console.log('[Payment Flow Service] Dispatching LockOffer action');
          this.store
            .dispatch(new LockOffer(payload))
            .pipe(take(1))
            .subscribe({
              next: () => {
                console.log(
                  '[Payment Flow Service] LockOffer action completed successfully',
                );
                Sentry.addBreadcrumb({
                  category: 'payment',
                  message: 'LockOffer action completed',
                  level: 'info',
                });

                offerLocked$.pipe(take(1)).subscribe((offerLocked) => {
                  console.log('[Payment Flow Service] Received locked offer:', {
                    id: offerLocked.id,
                    number: offerLocked.number,
                    hasPaymentUrl: !!offerLocked.paymentInitiationUrl,
                    signatureState: offerLocked.signatureState,
                  });

                  if (paymentMethod === 'PM_BT') {
                    console.log(
                      '[Payment Flow Service] Bank transfer payment method, redirecting to upload page',
                    );
                    Sentry.addBreadcrumb({
                      category: 'payment',
                      message: 'Redirecting to bank transfer upload page',
                      level: 'info',
                      data: { policyId: offerLocked.number },
                    });

                    this.router.navigateByUrl(
                      `/cta/upload/${offerLocked.number}`,
                    );
                  } else {
                    if (payload.signatureTypeCode === 'AUTHORIZED_BY_PAYMENT') {
                      console.log(
                        '[Payment Flow Service] Payment authorization signature type',
                      );
                      // Check for popup blockers before attempting to open payment window
                      const isPopupBlocked = this.checkPopupBlocker();
                      console.log(
                        `[Payment Flow Service] Popup blocker detected: ${isPopupBlocked}`,
                      );

                      if (isPopupBlocked) {
                        // Handle blocked popup situation
                        console.log(
                          '[Payment Flow Service] Popup blocked, showing payment modal',
                        );
                        Sentry.captureMessage(
                          `Payment popup blocked during policy signing for policy ${
                            offerLocked.number || 'unknown'
                          }`,
                        );

                        // Modal will be shown automatically from the state subscription
                        // We don't need to manually redirect to payment-redirect route

                        // Still start the polling to check for payment completion
                        console.log(
                          '[Payment Flow Service] Starting polling for payment status',
                        );
                        this.intervalId = setInterval(() => {
                          console.log(
                            `[Payment Flow Service] Polling for policy ${offerLocked.id} status`,
                          );
                          this.store.dispatch(new GetPolicy(offerLocked.id));
                        }, 5000);

                        return;
                      }

                      // Try to open payment in new tab if popups are allowed
                      console.log(
                        '[Payment Flow Service] Attempting to open payment in new window:',
                        offerLocked.paymentInitiationUrl,
                      );
                      const paymentTab = window.open(
                        offerLocked.paymentInitiationUrl,
                        '_blank',
                      );

                      if (
                        !paymentTab ||
                        paymentTab.closed ||
                        typeof paymentTab.closed === 'undefined'
                      ) {
                        console.error(
                          `[Payment Flow Service] POPUP BLOCKED for policy ${
                            offerLocked.number || 'unknown'
                          }!`,
                        );
                        Sentry.captureMessage(
                          `Payment popup blocked after check passed for policy ${
                            offerLocked.number || 'unknown'
                          }`,
                        );

                        // Modal will be shown automatically from the state subscription
                        console.log(
                          '[Payment Flow Service] Payment window blocked, showing modal',
                        );

                        // Still start the polling to check for payment completion
                        console.log(
                          '[Payment Flow Service] Starting polling for payment status',
                        );
                        this.intervalId = setInterval(() => {
                          console.log(
                            `[Payment Flow Service] Polling for policy ${offerLocked.id} status`,
                          );
                          this.store.dispatch(new GetPolicy(offerLocked.id));
                        }, 5000);

                        return;
                      }

                      // NOTE: Listening for payment confirmation
                      console.log(
                        '[Payment Flow Service] Payment window opened successfully, starting polling',
                      );
                      this.intervalId = setInterval(() => {
                        console.log(
                          `[Payment Flow Service] Polling for policy ${offerLocked.id} status`,
                        );
                        this.store.dispatch(new GetPolicy(offerLocked.id));
                      }, 5000);
                      return;
                    }

                    // AUTHORIZED_BY_SMS with confirmation workaround
                    const { confirmationCode, id } = offerLocked;
                    console.log(
                      '[Payment Flow Service] SMS authorization type, sending confirmation code',
                    );

                    const payloadDto = {
                      confirmationCode: confirmationCode,
                      confirmedOn: moment().format('YYYY-MM-DDTHH:mm:ssZ'),
                    };

                    Sentry.addBreadcrumb({
                      category: 'payment',
                      message: 'Dispatching SignOffer with SMS confirmation',
                      level: 'info',
                      data: { policyId: id },
                    });

                    this.store
                      .dispatch(new SignOffer(id, payloadDto))
                      .pipe(take(1))
                      .subscribe({
                        next: () => {
                          console.log(
                            '[Payment Flow Service] SignOffer successful, proceeding to payment',
                          );

                          // Check for popup blockers before attempting to open payment window
                          const isPopupBlocked = this.checkPopupBlocker();
                          console.log(
                            `[Payment Flow Service] Popup blocker for SMS flow detected: ${isPopupBlocked}`,
                          );

                          if (isPopupBlocked) {
                            // Modal will be shown automatically from the state subscription
                            console.log(
                              '[Payment Flow Service] SMS flow: Popup blocked, showing modal',
                            );

                            // Still start the polling to check for payment completion
                            console.log(
                              '[Payment Flow Service] SMS flow: Starting polling for payment status',
                            );
                            this.intervalId = setInterval(() => {
                              console.log(
                                `[Payment Flow Service] SMS flow: Polling for policy ${id} status`,
                              );
                              this.store.dispatch(new GetPolicy(id));
                            }, 5000);

                            return;
                          }

                          // Try to open in new tab
                          console.log(
                            '[Payment Flow Service] SMS flow: Attempting to open payment window',
                          );

                          // Ensure the URL is properly serialized
                          let paymentUrl = offerLocked.paymentInitiationUrl;

                          try {
                            // First try to parse the URL to validate it
                            const urlObj = new URL(paymentUrl);

                            // For external URLs, we should only sanitize, not use Angular's routing
                            // We'll keep the original URL, but log that we've validated it
                            console.log(
                              '[Payment Flow Service] Payment URL is valid:',
                              urlObj.href,
                            );

                            // Sanitize the URL to prevent XSS
                            paymentUrl = this.sanitizeUrl(paymentUrl);
                          } catch (error) {
                            console.error(
                              '[Payment Flow Service] Invalid payment URL:',
                              error,
                            );
                            Sentry.captureException(error);
                            // Fallback to the original URL if parsing fails
                          }

                          const paymentTab = window.open(
                            paymentUrl,
                            '_blank',
                            'noopener,noreferrer',
                          );

                          if (
                            !paymentTab ||
                            paymentTab.closed ||
                            typeof paymentTab.closed === 'undefined'
                          ) {
                            // Modal will be shown automatically from the state subscription
                            console.log(
                              '[Payment Flow Service] SMS flow: Payment window blocked, showing modal',
                            );

                            // Still start the polling to check for payment completion
                            console.log(
                              '[Payment Flow Service] SMS flow: Starting polling for payment status',
                            );
                            this.intervalId = setInterval(() => {
                              console.log(
                                `[Payment Flow Service] SMS flow: Polling for policy ${id} status`,
                              );
                              this.store.dispatch(new GetPolicy(id));
                            }, 5000);
                          } else {
                            // Start the polling to check for payment completion
                            console.log(
                              '[Payment Flow Service] SMS flow: Payment window opened, starting polling',
                            );
                            this.intervalId = setInterval(() => {
                              console.log(
                                `[Payment Flow Service] SMS flow: Polling for policy ${id} status`,
                              );
                              this.store.dispatch(new GetPolicy(id));
                            }, 5000);
                          }
                        },
                        error: (err) => {
                          console.error(
                            '[Payment Flow Service] SignOffer error:',
                            err,
                          );
                          Sentry.captureException(err);
                        },
                      });
                  }
                });
              },
              error: (err) => {
                console.error(
                  '[Payment Flow Service] Error locking offer:',
                  err,
                );
                Sentry.captureException(err);
              },
            });
        } else {
          console.error(
            '[Payment Flow Service] Failed to create offer lock payload',
          );
          Sentry.captureMessage('Failed to create offer lock payload');
        }
      });
  }

  uploadPolicyDocuments(policyId: number, payload: FormData): Observable<void> {
    return this.http.post<void>(
      `${this.apiBase}/idefend/policies/${policyId}/uploads`,
      payload,
    );
  }

  getDiscountCode(discountCode: string): Observable<any> {
    return this.http.get(`${this.apiBase}/discount/code/${discountCode}`);
  }

  getDiscountFromLink(discountLink: string): Observable<any> {
    return this.http.get(`${this.apiBase}/check-link/${discountLink}`);
  }

  // Private method to sanitize URLs
  private sanitizeUrl(url: string): string {
    // Only allow http and https protocols
    if (url.startsWith('http://') || url.startsWith('https://')) {
      return url;
    } else if (url.includes('://')) {
      // If it has another protocol, remove it
      console.warn('[Payment Flow Service] Removed unsafe protocol from URL');
      Sentry.captureMessage('Removed unsafe protocol from payment URL');
      return 'https://' + url.split('://')[1];
    } else {
      // If no protocol, assume https
      return 'https://' + url;
    }
  }
}
